import React, { useState, useCallback } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui';
import { PracticeManagementProvider, usePracticeManagement } from '../../contexts/PracticeManagementContext';
import PMOverviewTab from './PMOverviewTab';
import PMPriorityQueueTab from './PMPriorityQueueTab';
import PMTaskLibraryTab from './PMTaskLibraryTab';
import PMChecklistsTab from './PMChecklistsTab';
import PMReportingTab from './PMReportingTab';
import InboxShell from './inbox/InboxShell';
import { useInboxState } from './inbox/useInboxState';
import CompletionModal from '../shared/CompletionModal';
import SkipReasonModal from '../shared/SkipReasonModal';
import DelegateModal from '../shared/DelegateModal';
import OrphanedTaskModal from '../shared/OrphanedTaskModal';

function PracticeManagementPage({ readOnly = false }) {
  return (
    <PracticeManagementProvider readOnly={readOnly}>
      <PracticeManagementInner readOnly={readOnly} />
    </PracticeManagementProvider>
  );
}

function PracticeManagementInner({ readOnly }) {
  const { completeTask, skipTask, delegateTask } = usePracticeManagement();
  const { folderCounts: emailCounts } = useInboxState();
  const [activeTab, setActiveTab] = useState('overview');
  const isOverview = activeTab === 'overview';
  const isQueue = activeTab === 'queue';
  const isLibrary = activeTab === 'library';
  const isChecklists = activeTab === 'checklists';
  const isEmail = activeTab === 'email';
  const isFixedHeight = isQueue || isLibrary || isChecklists || isEmail;

  // ── Cross-tab filter state ──
  // When Overview clicks a metric, it sets these before switching tabs.
  // Target tabs read initialFilter on mount/tab-switch and apply it.
  const [crossFilter, setCrossFilter] = useState(null);

  const navigateTo = useCallback((tab, filter = null) => {
    setCrossFilter(filter);
    setActiveTab(tab);
  }, []);

  // Clear cross-filter when user manually switches tabs
  const handleTabChange = useCallback((tab) => {
    setCrossFilter(null);
    setActiveTab(tab);
  }, []);

  // Modal state
  const [completeOpen, setCompleteOpen] = useState(false);
  const [skipOpen, setSkipOpen] = useState(false);
  const [delegateOpen, setDelegateOpen] = useState(false);
  const [orphanedOpen, setOrphanedOpen] = useState(false);
  const [modalTask, setModalTask] = useState(null);

  const handleOpenComplete = useCallback((task) => {
    setModalTask(task);
    setCompleteOpen(true);
  }, []);

  const handleOpenSkip = useCallback((task) => {
    setModalTask(task);
    setSkipOpen(true);
  }, []);

  const handleOpenDelegate = useCallback((task) => {
    setModalTask(task);
    setDelegateOpen(true);
  }, []);

  const handleOpenOrphaned = useCallback(() => {
    setOrphanedOpen(true);
  }, []);

  const handleComplete = useCallback((id, opts) => {
    completeTask(id, opts);
  }, [completeTask]);

  const handleSkip = useCallback((id, reason) => {
    skipTask(id, reason);
  }, [skipTask]);

  const handleDelegate = useCallback((id, delegatee) => {
    delegateTask(id, delegatee);
  }, [delegateTask]);

  return (
    <div className={isFixedHeight ? 'dashboard-container !overflow-hidden' : 'dashboard-container'}>
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className={isFixedHeight ? 'flex flex-col flex-1 min-h-0' : undefined}
      >
        <TabsList className="justify-start shrink-0">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="queue">Priority Queue</TabsTrigger>
          <TabsTrigger value="library">Task Library</TabsTrigger>
          <TabsTrigger value="checklists">Workflows</TabsTrigger>
          <TabsTrigger value="reporting">Reporting</TabsTrigger>
          <TabsTrigger value="email" className="relative">
            Email
            {(emailCounts.unread.inbox ?? 0) > 0 && (
              <span
                className="ml-1 inline-flex items-center justify-center rounded-full text-white"
                style={{ fontSize: 10, fontWeight: 700, lineHeight: 1, padding: '2px 5px', background: 'var(--red-critical)' }}
              >
                {emailCounts.unread.inbox}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className={isOverview ? 'flex-1 min-h-0 flex flex-col overflow-y-auto' : undefined}>
          <PMOverviewTab readOnly={readOnly} navigateTo={navigateTo} />
        </TabsContent>
        <TabsContent value="queue" className={isQueue ? 'flex-1 min-h-0 flex flex-col' : undefined}>
          <PMPriorityQueueTab
            readOnly={readOnly}
            initialFilter={activeTab === 'queue' ? crossFilter : null}
            navigateTo={navigateTo}
            onOpenComplete={handleOpenComplete}
            onOpenSkip={handleOpenSkip}
            onOpenDelegate={handleOpenDelegate}
            onOpenOrphaned={handleOpenOrphaned}
          />
        </TabsContent>
        <TabsContent value="library" className={isLibrary ? 'flex-1 min-h-0 flex flex-col' : undefined}>
          <PMTaskLibraryTab readOnly={readOnly} />
        </TabsContent>
        <TabsContent value="checklists" className={isChecklists ? 'flex-1 min-h-0 flex flex-col relative' : undefined}>
          <PMChecklistsTab readOnly={readOnly} initialWorkflowId={crossFilter?.workflowId} />
        </TabsContent>
        <TabsContent value="reporting">
          <PMReportingTab readOnly={readOnly} initialFilter={activeTab === 'reporting' ? crossFilter : null} />
        </TabsContent>
        <TabsContent value="email" className={isEmail ? 'flex-1 min-h-0 flex flex-col' : undefined}>
          <InboxShell style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }} />
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <CompletionModal
        open={completeOpen}
        onOpenChange={setCompleteOpen}
        task={modalTask}
        onComplete={handleComplete}
      />
      <SkipReasonModal
        open={skipOpen}
        onOpenChange={setSkipOpen}
        task={modalTask}
        onSkip={handleSkip}
      />
      <DelegateModal
        open={delegateOpen}
        onOpenChange={setDelegateOpen}
        task={modalTask}
        onDelegate={handleDelegate}
      />
      <OrphanedTaskModal
        open={orphanedOpen}
        onOpenChange={setOrphanedOpen}
      />
    </div>
  );
}

export default PracticeManagementPage;
